
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useEffect } from "react";
import CanonicalTag from "@/components/CanonicalTag";
import { NeumorphicThemeProvider, useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import NeumorphicThemeToggle from "@/components/NeumorphicThemeToggle";
import { NeumorphicContainer } from "@/components/neumorphic";

const WhyUDAContent = () => {
  const { theme } = useNeumorphicTheme();
  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-gray-900';
  const mutedTextColor = isDark ? 'text-gray-300' : 'text-gray-600';
  const recessedStyles = isDark 
    ? 'bg-[#0a0a0a] shadow-[inset_8px_8px_16px_#000000,inset_-8px_-8px_16px_#141414]' 
    : 'bg-[#f0f0f0] shadow-[inset_8px_8px_16px_#d8d8d8,inset_-8px_-8px_16px_#ffffff]';
  const raisedStyles = isDark
    ? 'bg-[#0a0a0a] shadow-[6px_6px_12px_#000000,-6px_-6px_12px_#141414]'
    : 'bg-white shadow-[6px_6px_12px_#d8d8d8,-6px_-6px_12px_#ffffff]';

  useEffect(() => {
    // Set page title and meta description
    document.title = "Why UDA | Guaranteed ROI ADU Builder";
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Find out why UDA ADU is California\'s #1 strategic ADU builder. From pre-permitted plans to ROI guarantees, we turn backyards into boardroom strategies.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Find out why UDA ADU is California\'s #1 strategic ADU builder. From pre-permitted plans to ROI guarantees, we turn backyards into boardroom strategies.';
      document.head.appendChild(meta);
    }

    // Add meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'best ADU builder California, ADU with ROI guarantee, strategic ADU, wealth-focused ADU construction');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'best ADU builder California, ADU with ROI guarantee, strategic ADU, wealth-focused ADU construction';
      document.head.appendChild(meta);
    }

    // Add canonical URL
    const canonicalLink = document.createElement('link');
    canonicalLink.rel = 'canonical';
    canonicalLink.href = 'https://www.udaadu.com/why-uda';
    document.head.appendChild(canonicalLink);

    // Add WebPage structured data
    const whyUdaSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Why UDA",
      "description": "Explore why UDA ADU is the only builder offering ROI-guaranteed ADU solutions — from design to permitting and construction.",
      "url": "https://www.udaadu.com/why-uda"
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(whyUdaSchema);
    document.head.appendChild(script);

    // Cleanup function to remove added elements when component unmounts
    return () => {
      const existingCanonical = document.querySelector('link[rel="canonical"][href="https://www.udaadu.com/why-uda"]');
      if (existingCanonical) {
        existingCanonical.remove();
      }
      
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd && existingJsonLd.textContent?.includes('"url": "https://www.udaadu.com/why-uda"')) {
        existingJsonLd.remove();
      }
    };
  }, []);

  return (
    <NeumorphicContainer className="min-h-screen">
      <CanonicalTag url="https://www.udaadu.com/why-uda" />
      <Navigation />
      <NeumorphicThemeToggle />
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Main Content in Recessed Container */}
        <div className={`rounded-2xl p-8 md:p-12 transition-all duration-300 ${recessedStyles}`}>
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-[#e4683b] mb-6">
              The Only ADU Company That Guarantees Your ROI Before Breaking Ground.
            </h1>
            <p className={`text-xl md:text-2xl ${mutedTextColor} font-light mb-8`}>
              We're not general contractors. We're financial strategists in tool belts.
            </p>
          </div>

          {/* Main Content */}
          <div className="mb-16">
            <p className={`text-lg ${mutedTextColor} leading-relaxed mb-12 text-center max-w-3xl mx-auto`}>
              Why do Bay Area families, tech founders, and real estate investors choose UDA ADU? Because we design for performance, not just beauty. We back every floor plan and permit set with revenue modeling and regional zoning precision. You don't build until we prove your return.
            </p>
          </div>

          {/* Feature Checklist - Raised Cards */}
          <div className="mb-16">
            <div className="grid gap-6 max-w-2xl mx-auto">
              {[
                "Pre-permitted ADU plans for faster build times",
                "AI-powered property screening & ROI scoring",
                "Real-time ADU cost calculator",
                "In-house permitting + compliance experts",
                "Strategic floorplans designed for passive income",
                "Zoning analysis + site planning in every consultation"
              ].map((feature, index) => (
                <div key={index} className={`flex items-start gap-4 p-5 rounded-xl transition-all duration-300 ${raisedStyles}`}>
                  <Check className="h-6 w-6 text-[#e4683b] flex-shrink-0 mt-0.5" />
                  <p className={`${isDark ? 'text-white' : 'text-gray-900'} text-lg`}>{feature}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Block */}
          <div className="text-center mb-16">
            <blockquote className="text-2xl md:text-3xl font-light text-[#e4683b] italic">
              "Turning backyards into boardroom strategies."
            </blockquote>
          </div>

          {/* Call to Action Section */}
          <div className="text-center">
            <h2 className={`text-2xl md:text-3xl font-bold ${textColor} mb-8`}>
              When your property needs a CFO, not just a contractor — it's time to call UDA.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/adu-calculator" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>Get My ADU ROI Score</Link>
              <Link to="/contact-us" className={`px-8 py-3 text-lg rounded-xl font-medium transition-all duration-300 ${
                isDark
                  ? 'bg-[#0a0a0a] text-[#e4683b] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414] hover:shadow-[10px_10px_20px_#000000,-10px_-10px_20px_#141414]'
                  : 'bg-white text-[#e4683b] shadow-[8px_8px_16px_#d8d8d8,-8px_-8px_16px_#ffffff] hover:shadow-[10px_10px_20px_#d0d0d0,-10px_-10px_20px_#ffffff]'
              }`}>Book a Free Call</Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </NeumorphicContainer>
  );
};

const WhyUDA = () => {
  return (
    <NeumorphicThemeProvider>
      <WhyUDAContent />
    </NeumorphicThemeProvider>
  );
};

export default WhyUDA;
